import { useOperations } from './useOperations';
import { useOperationIntent } from './useOperationIntent';
import { operationsRpc } from '@/lib/operations';
import { refreshSaleConsumers } from '@/lib/refreshSaleConsumers';
// ─────────────────────────────────────────────
// KLOTHIQ — Bulk Product Import commit mutation
// Wraps the commit_bulk_product_import RPC (spec FEAT-BULK-IMPORT-001 §6).
// Parsing/validation happens entirely client-side before this is ever
// called — see bulkImportValidation.ts. This hook only fires once,
// with the already-approved payload, and never partially.
// ─────────────────────────────────────────────

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { PRODUCTS_KEY } from './useProducts';

export interface BulkImportCommitPayload {
  product_type_id: string;
  file_name: string;
  products: unknown[];
  schema_version_id?: string | null;
  jersey_entry_version?: 2;
  configuration_version_id?: string;
}

export interface BulkImportCommitResult {
  success: boolean;
  products_created: number;
  products_updated: number;
  variants_created: number;
  variants_updated: number;
  recovered_groups?: number;
  groups?: {
    product_code: string;
    group_key?: string;
    success: boolean;
    recovered?: boolean;
    error?: string;
    variants?: { variant_id: string; sku: string }[];
  }[];
  product_ids: string[];
  variant_ids: string[];
}

export function useBulkImportCommit() {
  const queryClient = useQueryClient();
  const operations = useOperations();
  const intent = useOperationIntent('catalog-import');
  const origin = useOperationIntent('jersey-import-origin');

  return useMutation({
    mutationFn: async (payload: BulkImportCommitPayload) => {
      if (payload.jersey_entry_version === 2) {
        if (operations.data?.modules.jersey_entry !== 2)
          throw new Error('The optional jersey entry database update is required.');
        const result = await operationsRpc<BulkImportCommitResult & { contract_version: number }>(
          'commit_jersey_import_v2',
          {
            p_data: {
              ...payload,
              contract_version: 2,
              request_key: intent.key('commit'),
              origin_request_key: origin.key('import'),
            },
          },
        );
        if (!result.success || result.contract_version !== 2)
          throw new Error(
            'Import could not be confirmed. Recover the original import before retrying.',
          );
        intent.complete('commit');
        if (!result.groups?.some((g) => !g.success)) origin.complete('import');
        return result;
      }
      if (operations.data?.modules.catalog === 1) {
        const result = await operationsRpc<BulkImportCommitResult>('commit_catalog_import_v1', {
          p_data: { ...payload, contract_version: 1, request_key: intent.key('commit') },
        });
        if (!result.success)
          throw new Error(
            'Import could not be confirmed. Retry the same file to recover its result.',
          );
        intent.complete('commit');
        return result;
      }
      const { data, error } = await supabase.rpc('commit_bulk_product_import', {
        p_data: payload,
      });
      if (error) {
        console.error('[commit_bulk_product_import] Supabase RPC error:', error);
        const err = new Error(error.message || 'Import failed — no changes were made');
        Object.assign(err, error);
        throw err;
      }
      return data as BulkImportCommitResult;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PRODUCTS_KEY] });
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
      void refreshSaleConsumers(queryClient);
    },
  });
}
